import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FiPower, FiRefreshCw, FiTrendingUp } from "react-icons/fi";
import { ProfileFundCardEntry } from "./ProfileFundCard";
import { FundSelectionCard } from "../funds/FundSelectionCard";
import { HistoryTimeline } from "../history/HistoryTimeline";
import {
  formatFundCurrency,
  formatFundNumber,
  getFundSubscriptionNavLabel,
} from "../funds/fundCardUtils";
import { TadbirFund } from "../../hooks/useFunds";
import { useAuth } from "../../context/AuthContext";
import { tadbir } from "../../services/api";

const formatNumericInput = (value: string) => {
  const digitsOnly = String(value || "").replace(/[^\d]/g, "");
  return digitsOnly === "" ? "" : Number(digitsOnly).toLocaleString("en-US");
};

const formatPercentDisplay = (value: unknown, digits = 2) => {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) return "0";
  return Number.isInteger(numericValue)
    ? String(numericValue)
    : numericValue.toFixed(digits).replace(/\.?0+$/, "");
};

const formatPercentInputValue = (value: unknown) => {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) {
    return "0";
  }

  return String(Math.min(100, Math.max(0, Math.trunc(numericValue))));
};

type ProfileFundEntry = ProfileFundCardEntry;

export type CalculatorInputMode = "amount" | "unit";

const resolveSubscriptionNavLabel = (fund?: TadbirFund | null) =>
  getFundSubscriptionNavLabel(fund?.fundRegisterCode);

export const ReinvestModal = ({
  isOpen,
  onClose,
  fundProfile,
  onSubmitted,
}: {
  isOpen: boolean;
  onClose: () => void;
  fundProfile: ProfileFundEntry | null;
  onSubmitted: () => Promise<void>;
}) => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const [percent, setPercent] = React.useState("0");
  const [savedPercent, setSavedPercent] = React.useState(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [historyState, setHistoryState] = React.useState({
    items: [] as any[],
    totalData: 0,
    currentPage: 0,
    totalPage: 0,
    isLoading: false,
    isLoadingMore: false,
    error: "",
  });
  const cardBg = useColorModeValue("white", "dark.50");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const subtleBg = useColorModeValue("gray.50", "dark.900");
  const borderColor = useColorModeValue("gray.100", "gray.700");

  const canLoadHistory =
    hasPermission("investHistories:view") || hasPermission("profile:view");
  const fundSeoRegisterNo = String(fundProfile?.fundRegisterCode || "").trim();
  const currentPercent = savedPercent;
  const isActive = currentPercent > 0;

  React.useEffect(() => {
    if (isOpen) {
      setPercent(formatPercentInputValue(fundProfile?.reInvestPercent ?? 0));
      setSavedPercent(Number(fundProfile?.reInvestPercent || 0));
    }
  }, [fundProfile?.reInvestPercent, isOpen]);

  React.useEffect(() => {
    if (!isOpen || !fundSeoRegisterNo || !canLoadHistory) {
      setHistoryState((current) => ({
        ...current,
        items: [],
        totalData: 0,
        currentPage: 0,
        totalPage: 0,
        isLoading: false,
        isLoadingMore: false,
        error: "",
      }));
      return;
    }

    let isMounted = true;

    const loadInitialHistory = async () => {
      try {
        setHistoryState((current) => ({
          ...current,
          isLoading: true,
          isLoadingMore: false,
          error: "",
        }));

        const result = await tadbir.getReinvestHistory(fundSeoRegisterNo, {
          page: 1,
          pageSize: 20,
          sort: "asc",
        });

        if (!isMounted) {
          return;
        }

        setHistoryState({
          items: Array.isArray(result.history) ? result.history : [],
          totalData: Number(result.totalData || 0),
          currentPage: Number(result.currentPage || 1),
          totalPage: Number(result.totalPage || 0),
          isLoading: false,
          isLoadingMore: false,
          error: "",
        });
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        setHistoryState((current) => ({
          ...current,
          isLoading: false,
          isLoadingMore: false,
          error:
            error?.response?.data?.message ||
            error?.message ||
            "خطا در دریافت تاریخچه سرمایه‌گذاری مجدد",
        }));
      }
    };

    void loadInitialHistory();

    return () => {
      isMounted = false;
    };
  }, [canLoadHistory, fundSeoRegisterNo, isOpen]);

  const handleLoadMoreHistory = React.useCallback(async () => {
    if (
      !canLoadHistory ||
      !fundSeoRegisterNo ||
      historyState.isLoading ||
      historyState.isLoadingMore ||
      historyState.currentPage >= historyState.totalPage
    ) {
      return;
    }

    try {
      setHistoryState((current) => ({
        ...current,
        isLoadingMore: true,
        error: "",
      }));

      const nextPage = historyState.currentPage + 1;
      const result = await tadbir.getReinvestHistory(fundSeoRegisterNo, {
        page: nextPage,
        pageSize: 20,
        sort: "asc",
      });

      setHistoryState((current) => ({
        ...current,
        items: [
          ...current.items,
          ...(Array.isArray(result.history) ? result.history : []),
        ],
        totalData: Number(result.totalData || current.totalData || 0),
        currentPage: Number(result.currentPage || nextPage),
        totalPage: Number(result.totalPage || current.totalPage || 0),
        isLoadingMore: false,
      }));
    } catch (error: any) {
      setHistoryState((current) => ({
        ...current,
        isLoadingMore: false,
        error:
          error?.response?.data?.message ||
          error?.message ||
          "خطا در دریافت ادامه تاریخچه",
      }));
    }
  }, [
    canLoadHistory,
    fundSeoRegisterNo,
    historyState.currentPage,
    historyState.isLoading,
    historyState.isLoadingMore,
    historyState.totalPage,
  ]);

  const quickOptions = ["1", "50", "100"];

  const handleSubmit = async () => {
    if (!fundProfile) return;

    if (percent.trim() === "") {
      toast({
        title: "درصد معتبر نیست",
        description: "برای ثبت، یک عدد بین ۰ تا ۱۰۰ وارد کنید.",
        status: "warning",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    const value = Number(percent.replaceAll(",", ""));

    if (!fundSeoRegisterNo) {
      toast({
        title: "کد صندوق پیدا نشد",
        status: "warning",
        duration: 2500,
        position: "top",
        isClosable: true,
      });
      return;
    }

    if (!Number.isFinite(value) || value < 0 || value > 100) {
      toast({
        title: "درصد معتبر نیست",
        description: "درصد سرمایه‌گذاری مجدد باید بین ۰ تا ۱۰۰ باشد.",
        status: "warning",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await tadbir.updateReinvest({
        reInvestPercent: value,
        FundSeoRegisterNo: fundSeoRegisterNo,
      });
      toast({
        title:
          value === 0
            ? "سرمایه‌گذاری مجدد غیرفعال شد"
            : "درصد سرمایه‌گذاری مجدد بروزرسانی شد",
        status: "success",
        duration: 2500,
        position: "top",
        isClosable: true,
      });
      await onSubmitted();
      setSavedPercent(value);
      const refreshedHistory = await tadbir.getReinvestHistory(
        fundSeoRegisterNo,
        {
          page: 1,
          pageSize: 20,
          sort: "asc",
        },
      );
      setHistoryState({
        items: Array.isArray(refreshedHistory.history)
          ? refreshedHistory.history
          : [],
        totalData: Number(refreshedHistory.totalData || 0),
        currentPage: Number(refreshedHistory.currentPage || 1),
        totalPage: Number(refreshedHistory.totalPage || 0),
        isLoading: false,
        isLoadingMore: false,
        error: "",
      });
      if (value === 0) {
        setPercent("0");
      }
    } catch (error: any) {
      toast({
        title: "بروزرسانی انجام نشد",
        description:
          error?.response?.data?.message ||
          "در بروزرسانی درصد سرمایه‌گذاری مجدد خطایی رخ داد.",
        status: "error",
        duration: 3500,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (currentPercent <= 0) {
      setPercent("0");
      return;
    }

    setPercent("0");
    if (!fundProfile) return;

    try {
      setIsSubmitting(true);
      await tadbir.updateReinvest({
        reInvestPercent: 0,
        FundSeoRegisterNo: fundSeoRegisterNo,
      });
      toast({
        title: "سرمایه‌گذاری مجدد غیرفعال شد",
        status: "success",
        duration: 2500,
        position: "top",
        isClosable: true,
      });
      await onSubmitted();
      setSavedPercent(0);
      const refreshedHistory = await tadbir.getReinvestHistory(
        fundSeoRegisterNo,
        {
          page: 1,
          pageSize: 20,
          sort: "asc",
        },
      );
      setHistoryState({
        items: Array.isArray(refreshedHistory.history)
          ? refreshedHistory.history
          : [],
        totalData: Number(refreshedHistory.totalData || 0),
        currentPage: Number(refreshedHistory.currentPage || 1),
        totalPage: Number(refreshedHistory.totalPage || 0),
        isLoading: false,
        isLoadingMore: false,
        error: "",
      });
    } catch (error: any) {
      toast({
        title: "غیرفعال‌سازی انجام نشد",
        description:
          error?.response?.data?.message ||
          "در غیرفعال‌سازی سرمایه‌گذاری مجدد خطایی رخ داد.",
        status: "error",
        duration: 3500,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const investHistoryEventMeta: Record<string, string> = {
    activated: "فعال‌سازی سرمایه‌گذاری مجدد",
    deactivated: "غیرفعال‌سازی سرمایه‌گذاری مجدد",
    updated: "بروزرسانی درصد سرمایه‌گذاری مجدد",
  };

  const investHistorySourceMeta: Record<string, string> = {
    reinvest_modal: "مدیریت سود",
    tadbir: "تدبیر",
  };

  const investHistoryFieldMeta: Record<string, string> = {
    reInvest: "وضعیت",
    reInvestPercent: "درصد سرمایه‌گذاری مجدد",
    fundName: "نام صندوق",
    fundSeoRegisterNo: "شناسه صندوق",
  };

  const formatInvestHistoryValue = (field: string, value: any) => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }

    if (field === "reInvest") {
      return value ? "فعال" : "غیرفعال";
    }

    if (field === "reInvestPercent") {
      return `${Number(value).toLocaleString("fa-IR")}٪`;
    }

    return String(value);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="3xl" bg={cardBg} mx={4} overflow="hidden">
        <ModalHeader pt={6}>
          تعیین درصد سرمایه‌گذاری مجدد
          <Text mt={2} fontSize="sm" fontWeight="normal" color={mutedColor}>
            {fundProfile?.fundName || "صندوق انتخاب‌شده"}
          </Text>
        </ModalHeader>
        <ModalCloseButton top={4} insetInlineStart={4} insetInlineEnd="auto" />
        <ModalBody pb={2}>
          <Tabs isFitted>
            <TabList mb={4}>
              <Tab>فرم سرمایه‌گذاری مجدد</Tab>
              <Tab>
                <HStack spacing={2}>
                  <Text>تاریخچه</Text>
                  <Text fontSize="sm" color={mutedColor}>
                    {Number(
                      historyState.totalData || historyState.items.length,
                    ).toLocaleString("fa-IR")}
                  </Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <Box
                    bg={subtleBg}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={borderColor}
                    p={4}
                  >
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm" fontWeight="bold">
                        وضعیت فعلی: {isActive ? "فعال" : "غیرفعال"}
                      </Text>
                      <Text fontSize="sm" color={mutedColor} lineHeight="1.9">
                        برای فعال‌سازی، درصدی بیشتر از ۰ ثبت کنید. برای
                        غیرفعال‌سازی، مقدار را روی ۰ بگذارید.
                      </Text>
                      {/* <Text fontSize="sm" color={mutedColor}>
                        درصد فعلی: {formatPercentDisplay(currentPercent)}٪
                      </Text> */}
                    </VStack>
                  </Box>

                  <HStack spacing={2} flexWrap="wrap">
                    <Button
                      size="sm"
                      variant={percent === "0" ? "solid" : "outline"}
                      borderRadius="full"
                      onClick={() => setPercent("0")}
                    >
                      ۰٪
                    </Button>
                    {quickOptions.map((option) => (
                      <Button
                        key={option}
                        size="sm"
                        variant={percent === option ? "solid" : "outline"}
                        borderRadius="full"
                        onClick={() => setPercent(option)}
                      >
                        {option}٪
                      </Button>
                    ))}
                  </HStack>

                  <Input
                    value={percent}
                    onChange={(event) => {
                      const digitsOnly = event.target.value.replace(
                        /[^\d]/g,
                        "",
                      );

                      if (digitsOnly === "") {
                        setPercent("");
                        return;
                      }

                      const numericValue = Math.min(Number(digitsOnly), 100);
                      setPercent(numericValue.toLocaleString("en-US"));
                    }}
                    placeholder="مثلاً 50"
                    size="lg"
                    borderRadius="2xl"
                    inputMode="numeric"
                  />
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <HistoryTimeline
                  title="تاریخچه سرمایه‌گذاری مجدد"
                  emptyMessage="هنوز رویدادی برای این صندوق ثبت نشده است."
                  state={historyState}
                  onLoadMore={handleLoadMoreHistory}
                  eventLabelMap={investHistoryEventMeta}
                  sourceLabelMap={investHistorySourceMeta}
                  fieldLabelMap={investHistoryFieldMeta}
                  formatValue={formatInvestHistoryValue}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter pb={6}>
          <VStack w="100%" spacing={3}>
            <HStack w="100%" spacing={3}>
              <Button
                flex="1"
                borderRadius="full"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="در حال ثبت"
              >
                {Number(percent.replaceAll(",", "") || 0) === 0
                  ? "ثبت"
                  : "ثبت درصد"}
              </Button>
              <Button
                flex="1"
                borderRadius="full"
                variant="outline"
                leftIcon={<Icon as={FiPower as any} />}
                onClick={handleDeactivate}
                isLoading={isSubmitting}
                isDisabled={currentPercent <= 0}
              >
                غیرفعال‌سازی
              </Button>
            </HStack>
            <Button
              w="100%"
              variant="ghost"
              borderRadius="full"
              onClick={onClose}
            >
              انصراف
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const FundCalculatorModal = ({
  isOpen,
  onClose,
  fund,
  amount,
  inputMode,
  onAmountChange,
  onInputModeChange,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  fund: TadbirFund | null;
  amount: string;
  inputMode: CalculatorInputMode;
  onAmountChange: (value: string) => void;
  onInputModeChange: (mode: CalculatorInputMode) => void;
  onSubmit: (fundRegisterCode?: string, amountInRial?: number) => void;
}) => {
  const cardBg = useColorModeValue("white", "dark.50");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const subtleBg = useColorModeValue("gray.50", "dark.900");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const positiveColor = useColorModeValue("green.600", "green.300");
  const subscriptionNavLabel = resolveSubscriptionNavLabel(fund);

  const normalizedInvestmentAmount = React.useMemo(() => {
    const parsedValue = Number(
      String(amount || "")
        .replaceAll(",", "")
        .trim(),
    );
    if (!Number.isFinite(parsedValue)) return 0;

    if (inputMode === "unit") {
      return parsedValue * Number(fund?.nav?.subscriptionNav || 0);
    }

    return parsedValue;
  }, [amount, fund?.nav?.subscriptionNav, inputMode]);

  const normalizedUnitCount = React.useMemo(() => {
    const parsedValue = Number(
      String(amount || "")
        .replaceAll(",", "")
        .trim(),
    );
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }, [amount]);

  const calculatorStats = React.useMemo(() => {
    if (!fund) {
      return null;
    }

    const subscriptionNav = Number(fund.nav?.subscriptionNav || 0);
    const annualReturn = Number(fund.annualReturn || 0);
    const dailyReturn = Number(fund.dailyReturn || 0) || annualReturn / 365;
    const unitCount =
      inputMode === "unit"
        ? normalizedUnitCount
        : subscriptionNav > 0
          ? Math.floor(normalizedInvestmentAmount / subscriptionNav)
          : 0;
    const estimatedDailyProfit =
      (normalizedInvestmentAmount * dailyReturn) / 100;
    const estimatedAnnualProfit =
      (normalizedInvestmentAmount * annualReturn) / 100;
    const estimatedMonthlyProfit = estimatedAnnualProfit / 12;

    return {
      subscriptionNav,
      annualReturn,
      dailyReturn,
      unitCount,
      estimatedDailyProfit,
      estimatedMonthlyProfit,
      estimatedAnnualProfit,
    };
  }, [fund, inputMode, normalizedInvestmentAmount, normalizedUnitCount]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="3xl" bg={cardBg} mx={4} overflow="hidden">
        <ModalHeader pt={6}>ماشین حساب خرید واحد</ModalHeader>
        <ModalCloseButton top={4} insetInlineStart={4} insetInlineEnd="auto" />
        <ModalBody pb={6}>
          {fund ? (
            <VStack align="stretch" spacing={5}>
              <FundSelectionCard
                fundData={fund}
                minHeight="96px"
                logoSize="60px"
              />

              <VStack align="stretch" spacing={3}>
                <FormControl>
                  <HStack justify="space-between" mb={2}>
                    <FormLabel fontSize="sm" textAlign="center" mb={0}>
                      {inputMode === "amount"
                        ? "مبلغ سرمایه‌گذاری (ریال)"
                        : "تعداد واحد"}
                    </FormLabel>
                    <Button
                      size="sm"
                      variant="ghost"
                      borderRadius="full"
                      rightIcon={<Icon as={FiRefreshCw as any} />}
                      onClick={() => {
                        onAmountChange("");
                        onInputModeChange(
                          inputMode === "amount" ? "unit" : "amount",
                        );
                      }}
                    >
                      {inputMode === "amount" ? "ریال به واحد" : "واحد به ریال"}
                    </Button>
                  </HStack>
                  <Input
                    size="lg"
                    textAlign={"center"}
                    borderRadius="2xl"
                    height={"55px"}
                    inputMode="numeric"
                    placeholder={
                      inputMode === "amount"
                        ? "مبلغ موردنظر خود برای سرمایه گذاری را وارد نمایید"
                        : "تعداد واحد موردنظر را وارد نمایید"
                    }
                    value={amount}
                    onChange={(event) =>
                      onAmountChange(formatNumericInput(event.target.value))
                    }
                  />
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <Box
                    bg="green.50"
                    color="green.700"
                    px={4}
                    py={3}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    _dark={{ bg: "green.900", color: "green.100" }}
                  >
                    <Text fontSize="sm" fontWeight="800">
                      {subscriptionNavLabel}:{" "}
                      {formatFundNumber(fund.nav?.subscriptionNav || 0)} ریال
                    </Text>
                  </Box>
                  <Box
                    bg="blue.50"
                    color="blue.700"
                    px={4}
                    py={3}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    _dark={{ bg: "blue.900", color: "blue.100" }}
                  >
                    <Text fontSize="sm" fontWeight="800">
                      بازده سالانه: {formatPercentDisplay(fund.annualReturn)}٪
                    </Text>
                  </Box>
                </SimpleGrid>
              </VStack>

              {calculatorStats ? (
                <>
                  <Box
                    bg={subtleBg}
                    borderRadius="3xl"
                    border="1px solid"
                    borderColor={borderColor}
                    p={{ base: 5, md: 6 }}
                    textAlign="center"
                  >
                    <Text fontSize="sm" color={mutedColor} mb={3}>
                      {inputMode === "amount" ? "واحد قابل خرید" : "ارزش ریالی"}
                    </Text>
                    <Text
                      fontSize={{ base: "4xl", md: "5xl" }}
                      lineHeight="1"
                      fontWeight="900"
                    >
                      {inputMode === "amount"
                        ? formatFundNumber(calculatorStats.unitCount)
                        : `${formatFundNumber(normalizedInvestmentAmount)} ریال`}
                    </Text>
                  </Box>

                  <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                    <Box
                      bg={subtleBg}
                      borderRadius="2xl"
                      p={4}
                      textAlign={"center"}
                    >
                      <Text fontSize="xs" color={mutedColor} mb={2}>
                        سود تخمینی سالانه
                      </Text>
                      <Text fontWeight="800" color={positiveColor}>
                        {formatFundCurrency(
                          calculatorStats.estimatedAnnualProfit,
                        )}{" "}
                        ریال
                      </Text>
                    </Box>
                    <Box
                      bg={subtleBg}
                      borderRadius="2xl"
                      p={4}
                      textAlign={"center"}
                    >
                      <Text fontSize="xs" color={mutedColor} mb={2}>
                        سود تخمینی ماهانه
                      </Text>
                      <Text fontWeight="900" color={positiveColor}>
                        {formatFundCurrency(
                          calculatorStats.estimatedMonthlyProfit,
                        )}{" "}
                        ریال
                      </Text>
                    </Box>
                    <Box
                      bg={subtleBg}
                      borderRadius="2xl"
                      p={4}
                      textAlign={"center"}
                    >
                      <Text fontSize="xs" color={mutedColor} mb={2}>
                        سود تخمینی روزانه
                      </Text>
                      <Text fontWeight="900" color={positiveColor}>
                        {formatFundCurrency(
                          calculatorStats.estimatedDailyProfit,
                        )}{" "}
                        ریال
                      </Text>
                    </Box>
                  </SimpleGrid>
                </>
              ) : null}
            </VStack>
          ) : null}
        </ModalBody>
        <ModalFooter pb={6}>
          <VStack w="100%" spacing={3}>
            <Button
              w="100%"
              h="50px"
              borderRadius="full"
              rightIcon={<Icon as={FiTrendingUp as any} />}
              onClick={() =>
                onSubmit(fund?.fundRegisterCode, normalizedInvestmentAmount)
              }
            >
              صدور {formatFundNumber(calculatorStats?.unitCount || 0)} واحد
            </Button>
            <Button
              w="100%"
              variant="ghost"
              borderRadius="full"
              onClick={onClose}
            >
              انصراف
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
